"use client";

import type { menuItem } from "@/components/blog/comments/comment-options";
import type { SortOption } from "@/components/blog/sort-selector";
import { ErrorBox } from "@/components/ui/ErrorBox";
import { LoadingBox } from "@/components/ui/LoadingBox";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUser } from "@/lib/auth/session";
import { deleteBookmark } from "@/lib/blog/bookmarks";
import { deleteComment } from "@/lib/blog/comments";
import { deleteReaction } from "@/lib/blog/reactions";
import type { EnrichedProfile } from "@/lib/supabase/types";
import { cn, copyToClipboard } from "@/lib/utils";
import { FlagIcon, Link2Icon, MoreVerticalIcon, TrashIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import React from "react";
import { toast } from "sonner";
import type { ProfileActivity } from "./profile-activity";

export const sortOptions: SortOption[] = [
	{ label: "Recent", value: "recent" },
	{ label: "Oldest", value: "oldest" },
];

export const activityOptions: menuItem[] = [
	{
		label: "Delete",
		icon: TrashIcon,
		group: "Activity",
		kbd: "⌘D",
	},
	{
		label: "Report",
		icon: FlagIcon,
		group: "Help",
		kbd: "⌘R",
	},
	{
		label: "Copy Link",
		icon: Link2Icon,
		group: "Help",
		kbd: "⌘C",
	},
];

interface ActivityOptionsProps {
	activity: ProfileActivity;
	profile: EnrichedProfile | null;
	className?: string;
}

export default function ActivityOptions(props: ActivityOptionsProps) {
	const { user, isUserLoading, error } = useUser();
	const router = useRouter();

	if (isUserLoading) {
		return <LoadingBox message="Loading your profile" />;
	}

	if (error) {
		return <ErrorBox error={error.toString()} />;
	}

	const checkPermission = (label: string) => {
		switch (label) {
			case "Delete":
				return props.profile?.id === user?.id;
			case "Report":
				return true;
			case "Copy Link":
				return true;
		}
	};

	const handleDelete = async () => {
		if (isUserLoading || !user?.id) return;

		let error: unknown | null = null;
		switch (props.activity.type) {
			case "comment":
				({ error } = await deleteComment(user.id, props.activity.id));
				break;
			case "reaction":
				({ error } = await deleteReaction(user.id, props.activity.id));
				break;
			case "bookmark":
				({ error } = await deleteBookmark(user.id, props.activity.post_id));
				break;
			default:
				error = new Error("Invalid activity type");
				break;
		}

		if (error) {
			console.error(error);
			toast("Failed to delete activity", {
				icon: <TrashIcon className="w-4 h-4" />,
				style: {
					background: "red",
					color: "white",
				},
			});
			return;
		}

		toast("Activity deleted successfully", {
			icon: <TrashIcon className="w-4 h-4" />,
			style: {
				background: "green",
				color: "white",
			},
		});

		router.refresh();
	};

	const handleReport = () => {
		// TODO: handle reporting
		toast("Thank you for reporting", {
			icon: <FlagIcon className="w-4 h-4" />,
			style: {
				background: "orange",
				color: "white",
			},
		});
	};

	const handleCopyLink = () => {
		if (!props.activity.link) return;
		copyToClipboard(props.activity.link.url);
		toast("Link copied to clipboard", {
			icon: <Link2Icon className="w-4 h-4" />,
			style: {
				background: "green",
				color: "white",
			},
		});
	};

	const getOnClick = (label: string) => {
		switch (label) {
			case "Delete":
				return handleDelete;
			case "Report":
				return handleReport;
			case "Copy Link":
				return handleCopyLink;
		}
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				asChild
				className={cn("flex items-center justify-center", props.className)}
			>
				<Button
					variant="outline"
					className="flex border-none shadow-none items-center justify-center p-2.5 rounded-full"
				>
					<MoreVerticalIcon className="size-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-42 mr-2" align="end">
				{activityOptions.map((item, index) =>
					checkPermission(item.label) ? (
						<React.Fragment key={`fragment-${item.label}-${index}`}>
							<DropdownMenuItem
								key={`item-${item.label}`}
								onClick={getOnClick(item.label)}
							>
								{item.icon && (
									<item.icon className="size-4 fill-white/30 group-hover:stroke-[3px]" />
								)}
								{item.label}
								<DropdownMenuShortcut>{item.kbd}</DropdownMenuShortcut>
							</DropdownMenuItem>
						</React.Fragment>
					) : null,
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
